import { useMediaQuery, Grid, Box, Typography } from '@mui/material';
import i18next from 'i18next';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompoundText } from '../../elements/compound-text';
import { VendorOrder, OrderStatus, ServiceMethod } from '../../types/order';
import { orderStatusConfig, VendorOrderCard } from '../vendor-order-card';
import { OrderStatusText } from './order-status-text';
import { Image } from '../../elements/image';
import { Text } from '../../elements/text';
import { OrderStatusStepper } from './order-status-stepper';
import itemImage from '../../assets/images/item-burger.jpg';
import { AppContext } from '../../context/app-context';
import TableBarIcon from '@mui/icons-material/TableBar';
import RoomServiceIcon from '@mui/icons-material/RoomService';

interface IOrderTrackingInfoProps {
  order: VendorOrder;
  orderNo: string;
  orderID: string;
  orderStatus: OrderStatus;
  isClickAndCollectEnabled: boolean;
  orderBannerImage?: string;
  index: number;
  serviceMethod: string;
  collectionPointLabel?: string;
  previousStatusHistory?: string;
}

export const OrderTrackingInfo = ({
  order,
  orderNo,
  orderID,
  orderStatus,
  isClickAndCollectEnabled,
  serviceMethod,
  orderBannerImage,
  index,
  collectionPointLabel,
  previousStatusHistory,
}: IOrderTrackingInfoProps) => {
  const { t } = useTranslation(['patron_portal', 'common']);
  const isNarrowScreen = useMediaQuery('(max-width: 410px)');
  const { seat } = useContext(AppContext);

  const getOrderTrackingText = () => {
    console.log(orderStatus);
    switch (orderStatus) {
      case (OrderStatus.PAYMENT_PENDING, OrderStatus.RECEIVED):
        return i18next.t('patron_portal.order.msgOrderStatusRecieved', {
          ns: 'patron_portal',
        });

      case OrderStatus.PREPARING:
        return i18next.t('patron_portal.order.msgOrderStatusPreparing', {
          ns: 'patron_portal',
        });

      case OrderStatus.ACCEPTED:
      case OrderStatus.ASSIGNED_BUT_PREPARING:
        return i18next.t('patron_portal.order.msgOrderStatusPreparing', {
          ns: 'patron_portal',
        });

      case OrderStatus.PREPARED: {
        let statusMessage = '';
        if (
          serviceMethod === ServiceMethod.ROOM_SERVICE ||
          serviceMethod === ServiceMethod.TABLE_SERVICE
        ) {
          statusMessage = 'patron_portal.order.readyForDelivery';
        } else {
          statusMessage =
            serviceMethod === ServiceMethod.COLLECT_FROM_VENDOR
              ? 'patron_portal.order.msgOrderStatusPreparedCNC'
              : serviceMethod === ServiceMethod.VENDOR_DELIVERY
              ? 'patron_portal.order.msgOrderStatusPreparedVD'
              : 'patron_portal.order.msgOrderStatusPreparedTTS';
        }
        return i18next.t(statusMessage, {
          ns: 'patron_portal',
        });
      }
      case OrderStatus.READY_TO_COLLECT:
        return i18next.t(
          'patron_portal.order.msgOrderStatusReadyToCollectFull',
          {
            ns: 'patron_portal',
          },
        );
      case OrderStatus.ASSIGNED:
        return i18next.t('patron_portal.order.msgOrderStatusAssignedFull', {
          ns: 'patron_portal',
        });

      case OrderStatus.PICKED:
        return i18next.t('patron_portal.order.msgOrderStatusPicked', {
          ns: 'patron_portal',
        });

      case OrderStatus.AT_COLLECTION_POINT:
        return i18next.t(
          'patron_portal.order.msgOrderStatusAtCollectionPoint',
          {
            ns: 'patron_portal',
          },
        );

      case OrderStatus.DELIVERED:
        return i18next.t('patron_portal.order.msgOrderStatusDelivered', {
          ns: 'patron_portal',
        });

      case OrderStatus.COMPLETE:
        return i18next.t('patron_portal.order.msgOrderStatusDelivered', {
          ns: 'patron_portal',
        });

      case OrderStatus.PATRON_CANCELLED:
        return i18next.t('patron_portal.order.msgOrderStatusPatronCancelled', {
          ns: 'patron_portal',
        });

      case OrderStatus.REJECTED:
        return i18next.t('patron_portal.order.msgOrderStatusRejected', {
          ns: 'patron_portal',
        });

      case OrderStatus.REFUNDED:
        return i18next.t('patron_portal.order.msgOrderStatusRefunded', {
          ns: 'patron_portal',
        });

      case OrderStatus.PAYMENT_REJECTED:
        return i18next.t('patron_portal.order.msgOrderStatusPaymentFailed', {
          ns: 'patron_portal',
        });

      default:
        return '';
    }
  };
  const orderStatusConfigItem = orderStatusConfig[orderStatus];

  const statusTextByServiceMethod =
    orderStatusConfigItem?.textByServiceMethod &&
    orderStatusConfigItem.textByServiceMethod(serviceMethod);

  const isReadyToCollectByPatron =
    orderStatus === OrderStatus.PREPARED &&
    serviceMethod === ServiceMethod.COLLECT_FROM_VENDOR;

  const validVendorImageStatuses = [
    OrderStatus.PREPARED,
    OrderStatus.ASSIGNED,
    OrderStatus.PICKED,
    OrderStatus.AT_COLLECTION_POINT,
    OrderStatus.COMPLETE,
    OrderStatus.PAYMENT_REJECTED,
  ];
  const showVendorImage = validVendorImageStatuses.includes(orderStatus);

  const hideVendorTradingName =
    (serviceMethod === ServiceMethod.COLLECTION_POINT &&
      (orderStatus === OrderStatus.ASSIGNED ||
        orderStatus === OrderStatus.PICKED ||
        orderStatus === OrderStatus.AT_COLLECTION_POINT)) ||
    orderStatus === OrderStatus.COMPLETE;

  return (
    <Grid container borderRadius={'8px'} position={'relative'}>
      <Grid
        container
        item
        xs={12}
        display={'flex'}
        margin={'auto'}
        position={'relative'}
      >
        <Box
          sx={{
            aspectRatio: '4/1',
            // TODO: change this minHeight before pushing
            minHeight: '100px',
          }}
          position={'relative'}
          borderRadius={'8px'}
          width={'100%'}
        >
          <Image
            src={
              order.store?.storeFrontImageUrl ||
              order.vendor?.imageUrl ||
              itemImage
            }
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '8px',
            }}
          />
        </Box>
        <Box
          sx={{
            backgroundColor: 'rgba(255,  255,  255,  0.8)',
            width: '100%',
          }}
          position={'absolute'}
          bottom={'0px'}
          borderRadius={'0px 0px 8px 8px'}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              my: '4px',
              mx: '8px',
              flexGrow: 1,
            }}
          >
            <Typography variant="vendorName">
              {order.vendor?.tradingName}
            </Typography>
            <CompoundText
              text={'Order'}
              boldText={orderNo}
              fontSize={isNarrowScreen ? 20 : 22}
            />
          </Box>
        </Box>
      </Grid>

      <Grid container item xs={12}>
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              flexWrap: 'nowrap',
              mt: '18px',
            }}
          >
            <OrderStatusText
              orderStatus={i18next.t(
                orderStatusConfig[orderStatus]?.text ||
                  statusTextByServiceMethod ||
                  '',
                {
                  ns: 'patron_portal',
                  collectionPoint: collectionPointLabel,
                },
              )}
            />
            {(order.serviceMethod === ServiceMethod.TABLE_SERVICE ||
              order.serviceMethod === ServiceMethod.ROOM_SERVICE) && (
              <Box
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                py={'4px'}
                px={'12px'}
                sx={{
                  color: 'white',
                  background: 'black',
                  borderRadius: '20px',
                }}
              >
                {order.serviceMethod === ServiceMethod.TABLE_SERVICE ? (
                  <TableBarIcon />
                ) : (
                  <RoomServiceIcon />
                )}
                <Box sx={{ marginLeft: '4px' }}>
                  <Typography variant="seatValue">{seat?.value}</Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Box>
            <Typography variant="orderTrackingText">
              {getOrderTrackingText()}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} marginY={3}>
          <OrderStatusStepper
            orderStatus={orderStatus}
            previousOrderStatus={previousStatusHistory}
          />
        </Grid>
      </Grid>
      <Grid container item xs={12}>
        <VendorOrderCard order={order} index={index} isPastOrder={false} />
      </Grid>
    </Grid>
  );
};
